/**
 * Section layout solver (ADR 0037).
 *
 * A "section" is a cabinetry unit along a wall — a typed opening (DH, LH,
 * hamper…) with a width, flanked by vertical panels. This module answers ONE
 * question, purely: given a wall run and an ordered list of sections, where
 * does every panel and opening land, and what doesn't fit?
 *
 * The arithmetic (owner, 2026-07-26, worked example: a 76" wall):
 *
 *   run L = endReveal + panel + opening₁ + panel + opening₂ + … + panel + endReveal
 *
 * - Each END of the run keeps a reveal (default ¾") between the outermost
 *   panel and the abutting side wall — scribe/clearance space.
 * - Every opening has a panel (default ¾") on BOTH sides. Adjacent sections
 *   SHARE the panel between them by default (two 36" openings need 3 panels,
 *   not 4); a per-join override (`fullPanelAfter`) forces a double panel where
 *   the units are built separately.
 * - LOCKED sections keep their exact typed width no matter what. UNLOCKED
 *   sections split the remaining opening space evenly — but never wider than
 *   the max-opening rule (default 36") unless that section carries an explicit
 *   `allowWide` override. Space that can't be used (everything at its cap, or
 *   everything locked) is reported as `leftoverUnits`, never silently absorbed.
 * - An unlocked section squeezed below the min-opening rule (default 18") is
 *   flagged `narrow` — the designer probably needs to remove it. If locked
 *   widths alone exceed the run, `overflowUnits` reports by how much; locked
 *   widths are still honoured (nothing is silently shrunk).
 *
 * Pure function, no model/DOM/state — unit-testable in isolation, and the same
 * code path feeds the plan render, the elevation render, and the side panel.
 */

const EPS = 1e-6;

/**
 * @typedef {object} SectionInput
 * @property {string} id
 * @property {number|null} widthUnits  Typed opening width; null when auto.
 * @property {boolean} locked          True = width is fixed by the designer.
 * @property {boolean} [fullPanelAfter] Join AFTER this section gets 2 panels.
 * @property {boolean} [allowWide]     Explicit override of the max-opening cap.
 * @property {'start'|'end'} [pinEnd]  This section must abut that end of the
 *   run — set for an L-shelf attached across the corresponding corner
 *   (ADR 0042 amendment, 2026-08-28). Only a TRAILING pinEnd:'end' section
 *   moves anything: the run's slack is laid BEFORE it so it sits flush at the
 *   end. Order is never changed — explicit beats inferred, so a pinned
 *   section the designer ordered elsewhere stays where they put it.
 * @property {number} [minOpeningUnits] Per-TYPE minimum for the narrow check,
 *   replacing the global rule for this section (a 13" double hang is fine
 *   when its type says 12" even though the global minimum is 18" — ADR 0039).
 *
 * @typedef {object} SectionLayout
 * @property {Array<{id:string, openingUnits:number, startUnits:number, locked:boolean,
 *   narrow:boolean, wide:boolean, atCap:boolean}>} items  One per section, in order.
 *   `startUnits` is the opening's left edge measured from the wall start.
 *   `narrow` = below min rule; `wide` = locked above max WITHOUT allowWide;
 *   `atCap` = unlocked and clamped to the max (so leftover exists elsewhere).
 * @property {Array<{startUnits:number, widthUnits:number}>} panels  Physical panels.
 * @property {number} runUnits
 * @property {number} usedUnits      2×reveal + panels + openings actually laid out.
 * @property {number} leftoverUnits  Unfillable slack (≥0). >0 means the wall is
 *   not fully consumed — add a section, widen with allowWide, or accept a gap.
 * @property {number} overflowUnits  Demand beyond the run (≥0). >0 means locked
 *   widths + panels + reveals physically don't fit.
 * @property {Array<{startUnits:number, widthUnits:number}>} gaps  Where the
 *   slack physically sits: after the last panel normally, or between the run
 *   and a pinned trailing section. Sums to `leftoverUnits`. Lets the plan
 *   label open wall spans with their real size.
 */

/**
 * Lays out `sections` (in order) along a wall run of `runUnits`.
 *
 * @param {number} runUnits  Interior wall length the sections must live within.
 * @param {SectionInput[]} sections
 * @param {{ sectionEndRevealUnits:number, sectionPanelThicknessUnits:number,
 *           sectionMinOpeningUnits:number, sectionMaxOpeningUnits:number }} rules
 * @param {{ startClearanceUnits?:number, endClearanceUnits?:number }} [opts]
 *   End CLEARANCES (ADR 0038): a hanging section abutting the corner on the
 *   perpendicular wall reserves this much of the run at that end — nothing
 *   (panel included) may sit inside it. A clearance replaces the reveal at
 *   its end (the larger of the two wins); 0/absent means just the reveal.
 * @returns {SectionLayout|null} null when there are no sections.
 */
export function layoutSections(runUnits, sections, rules, opts = {}) {
  const list = Array.isArray(sections) ? sections : [];
  if (list.length === 0) return null;
  const run = Number.isFinite(runUnits) && runUnits > 0 ? runUnits : 0;
  const reveal = num(rules?.sectionEndRevealUnits, 0.75);
  const panel = num(rules?.sectionPanelThicknessUnits, 0.75);
  const minOpen = num(rules?.sectionMinOpeningUnits, 18);
  const maxOpen = num(rules?.sectionMaxOpeningUnits, 36);
  const startClear = num(opts.startClearanceUnits, 0);
  const endClear = num(opts.endClearanceUnits, 0);
  // The reveal is scribe space for a PANEL against the abutting wall — an
  // OPEN section at the run's end has no panel there, so its space runs clean
  // to the wall (hang clearances still apply; they protect clothes, not trim).
  const openEnd = (s) => s?.open === true;
  const pinnedStart = list[0]?.pinEnd === 'start';
  const pinnedEnd = list[list.length - 1]?.pinEnd === 'end';
  const lead = Math.max(openEnd(list[0]) || pinnedStart ? 0 : reveal, startClear); // offset before the first panel
  const tail = Math.max(openEnd(list[list.length - 1]) || pinnedEnd ? 0 : reveal, endClear); // offset after the last panel

  // Panel widths between/around the openings: [lead, join₁ … joinₙ₋₁, tail].
  // A shared join is one panel; an overridden join is two back-to-back. OPEN
  // sections are deliberate empty space, and no panel is built SOLELY to
  // bound one: a lead/tail panel beside an open end is dropped, and a join
  // between two opens vanishes — but a join between an open and a real
  // section is the real section's side panel and stays.
  const isOpen = (s) => s?.open === true;
  // An attached L-shelf is FLUSH at its pinned corner (owner, 2026-08-28):
  // the unit has exactly two end panels — host far side and leg far end — and
  // a ¾" backer that turns the corner, so at the corner side there is no
  // outer panel and no reveal (a reveal is scribe space FOR a panel).
  const flushStart = list[0]?.pinEnd === 'start' && !isOpen(list[0]);
  const flushEnd = list[list.length - 1]?.pinEnd === 'end' && !isOpen(list[list.length - 1]);
  const panelWidths = [isOpen(list[0]) || flushStart ? 0 : panel];
  for (let i = 0; i < list.length - 1; i++) {
    panelWidths.push(isOpen(list[i]) && isOpen(list[i + 1]) ? 0 : list[i].fullPanelAfter ? panel * 2 : panel);
  }
  panelWidths.push(isOpen(list[list.length - 1]) || flushEnd ? 0 : panel);
  const panelTotal = panelWidths.reduce((a, b) => a + b, 0);

  // Space available to the openings themselves.
  const available = run - lead - tail - panelTotal;

  const locked = list.filter((s) => s.locked && Number.isFinite(s.widthUnits) && s.widthUnits > 0);
  const lockedSum = locked.reduce((a, s) => a + s.widthUnits, 0);
  const unlockedCount = list.length - locked.length;

  // Unlocked openings split what's left evenly, capped at the max-opening rule
  // per section unless that section is explicitly allowed wide. Sections at the
  // cap hand their surplus back to the uncapped ones (water-filling), so one
  // allowWide section can absorb slack the capped ones may not.
  const free = available - lockedSum;
  const autoIds = new Set(list.filter((s) => !(s.locked && Number.isFinite(s.widthUnits) && s.widthUnits > 0)).map((s) => s.id));
  const autoWidth = new Map(); // id -> opening
  if (unlockedCount > 0) {
    let pool = Math.max(0, free);
    let pending = list.filter((s) => autoIds.has(s.id));
    // Iterate: give everyone an even share; anyone over their cap gets pinned
    // at the cap and the remainder re-splits among the rest. OPEN sections are
    // never capped — empty space can be as wide as the wall allows.
    while (pending.length > 0) {
      const share = pool / pending.length;
      const capped = pending.filter((s) => !s.allowWide && !isOpen(s) && share > maxOpen + EPS);
      if (capped.length === 0) {
        for (const s of pending) autoWidth.set(s.id, share);
        break;
      }
      for (const s of capped) {
        autoWidth.set(s.id, maxOpen);
        pool -= maxOpen;
      }
      pending = pending.filter((s) => s.allowWide || isOpen(s));
      if (pending.length === 0) break; // everyone pinned; pool remainder is leftover
    }
  }

  // Walk the run start→end laying panels and openings.
  const items = [];
  const panels = [];
  let cursor = lead;
  if (panelWidths[0] > 0) panels.push({ startUnits: cursor, widthUnits: panelWidths[0] });
  cursor += panelWidths[0];
  list.forEach((s, i) => {
    const isLocked = s.locked && Number.isFinite(s.widthUnits) && s.widthUnits > 0;
    const opening = isLocked ? s.widthUnits : autoWidth.get(s.id) ?? 0;
    items.push({
      id: s.id,
      openingUnits: opening,
      startUnits: cursor,
      locked: isLocked,
      open: isOpen(s),
      narrow: !isOpen(s) && opening < (s.minOpeningUnits > 0 ? s.minOpeningUnits : minOpen) - EPS,
      wide: isLocked && !isOpen(s) && !s.allowWide && opening > maxOpen + EPS,
      atCap: !isLocked && !isOpen(s) && !s.allowWide && Math.abs(opening - maxOpen) < EPS && unlockedCount > 0,
    });
    cursor += opening;
    const pw = panelWidths[i + 1];
    if (pw > 0) panels.push({ startUnits: cursor, widthUnits: pw });
    cursor += pw;
  });
  const usedUnits = cursor + tail;
  const leftover = Math.max(0, run - usedUnits);

  // An attached L-shelf abuts its corner: when the TRAILING section(s) are
  // pinEnd:'end' and slack exists, the slack moves before them. The shift
  // starts at the first moved element — the pinned block's own left panel.
  // A double join (the L-shelf's never-shared panel pair, ADR 0042) splits:
  // the neighbour keeps its single, the L-shelf's half travels across the
  // gap. A single join is the pinned section's own side panel and travels
  // whole. Order is untouched; only positions move.
  const gaps = [];
  let firstPinned = list.length;
  while (firstPinned > 0 && list[firstPinned - 1]?.pinEnd === 'end') firstPinned--;
  if (leftover > EPS && firstPinned < list.length) {
    const blockStart = items[firstPinned].startUnits;
    const boundary = panels.find((p) => Math.abs(p.startUnits + p.widthUnits - blockStart) < EPS);
    let shiftFrom = blockStart;
    if (boundary) {
      if (boundary.widthUnits > panel + EPS) {
        const moving = boundary.widthUnits - panel;
        boundary.widthUnits = panel;
        panels.push({ startUnits: blockStart - moving, widthUnits: moving });
        shiftFrom = blockStart - moving;
      } else {
        shiftFrom = boundary.startUnits;
      }
    }
    for (const it of items) if (it.startUnits >= shiftFrom - EPS) it.startUnits += leftover;
    for (const p of panels) if (p.startUnits >= shiftFrom - EPS) p.startUnits += leftover;
    panels.sort((a, b) => a.startUnits - b.startUnits);
    gaps.push({ startUnits: shiftFrom, widthUnits: leftover });
  } else if (leftover > EPS) {
    gaps.push({ startUnits: cursor, widthUnits: leftover });
  }

  return {
    items,
    panels,
    runUnits: run,
    usedUnits,
    leftoverUnits: leftover,
    overflowUnits: Math.max(0, usedUnits - run),
    gaps,
    startClearanceUnits: startClear,
    endClearanceUnits: endClear,
  };
}

function num(v, fallback) {
  return Number.isFinite(v) && v >= 0 ? v : fallback;
}
